import { type ReactNode } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  type LucideIcon,
} from 'lucide-react';

type BadgeVariant =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'pending';
type BadgeSize = 'sm' | 'md' | 'lg';

interface StatusBadgeProps {
  variant: BadgeVariant;
  children: ReactNode;
  icon?: LucideIcon | 'auto';
  size?: BadgeSize;
  pulse?: boolean;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  success:
    'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400',
  warning:
    'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
  danger:
    'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400',
  info: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const autoIcons: Record<BadgeVariant, LucideIcon> = {
  success: CheckCircle,
  warning: AlertTriangle,
  danger: XCircle,
  info: AlertCircle,
  neutral: Clock,
  pending: Loader2,
};

const sizeStyles: Record<BadgeSize, { badge: string; icon: string }> = {
  sm: { badge: 'px-1.5 py-0.5 text-2xs gap-1', icon: 'w-3 h-3' },
  md: { badge: 'px-2 py-0.5 text-xs gap-1.5', icon: 'w-3.5 h-3.5' },
  lg: { badge: 'px-2.5 py-1 text-sm gap-1.5', icon: 'w-4 h-4' },
};

export function StatusBadge({
  variant,
  children,
  icon,
  size = 'md',
  pulse = false,
  className = '',
}: StatusBadgeProps) {
  const IconComponent = icon === 'auto' ? autoIcons[variant] : icon;
  const styles = sizeStyles[size];

  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium
        ${variantStyles[variant]}
        ${styles.badge}
        ${className}
      `}
    >
      {IconComponent && (
        <IconComponent
          className={`
            ${styles.icon}
            ${pulse && variant === 'pending' ? 'animate-spin' : ''}
          `}
        />
      )}
      {children}
      {pulse && variant !== 'pending' && (
        <span className="relative flex h-2 w-2 ml-1">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span>
        </span>
      )}
    </span>
  );
}

// Pre-configured status badges for common inspection statuses
export function InspectionStatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { variant: BadgeVariant; label: string }> =
    {
      scheduled: { variant: 'info', label: 'Scheduled' },
      submitted: { variant: 'warning', label: 'Submitted' },
      approved: { variant: 'success', label: 'Completed' },
      rejected: { variant: 'danger', label: 'Rejected' },
      draft: { variant: 'neutral', label: 'Draft' },
      completed: { variant: 'success', label: 'Completed' },
    };

  const config = statusConfig[status.toLowerCase()] || {
    variant: 'neutral' as BadgeVariant,
    label: status,
  };

  return (
    <StatusBadge variant={config.variant} icon="auto">
      {config.label}
    </StatusBadge>
  );
}

// Compliance score badge
export function ComplianceScoreBadge({ score }: { score: number }) {
  let variant: BadgeVariant = 'success';
  if (score < 70) variant = 'danger';
  else if (score < 85) variant = 'warning';

  return (
    <StatusBadge variant={variant} size="lg">
      {score}%
    </StatusBadge>
  );
}

// Priority badge for corrective actions
export function PriorityBadge({
  priority,
}: {
  priority:
    | 'critical'
    | 'high'
    | 'medium'
    | 'low'
    | 'resolved'
    | string
    | undefined;
}) {
  const config: Record<string, { variant: BadgeVariant; label: string }> = {
    critical: { variant: 'danger', label: 'Critical' },
    high: { variant: 'warning', label: 'High' },
    medium: { variant: 'info', label: 'Medium' },
    low: { variant: 'neutral', label: 'Low' },
    resolved: { variant: 'success', label: 'Resolved' },
  };

  // Default to medium if priority is undefined or unknown
  const priorityKey = priority && config[priority] ? priority : 'medium';
  const { variant, label } = config[priorityKey];

  return (
    <StatusBadge
      variant={variant}
      icon="auto"
      pulse={priorityKey === 'critical'}
    >
      {label}
    </StatusBadge>
  );
}
